import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import SuperAdminNav from '../components/SuperAdminNav';

const PLACEMENTS = [
  { label: 'Bottom', value: 'bottom' },
  { label: 'Top', value: 'top' },
  { label: 'Full Page', value: 'fullpage' },
];

const TARGET_ROLES = [
  { label: 'Coach', value: 'coach' },
  { label: 'Player', value: 'player' },
  { label: 'Parent', value: 'parent' },
];

const emptyForm = {
  name: '',
  placement: 'bottom',
  target_roles: ['coach', 'player', 'parent'],
  html_content: '',
  priority: 10,
  start_date: '',
  end_date: '',
  fullpage_timer_seconds: 5,
  fullpage_frequency: 10,
  status: 'active',
};

export default function SuperAds() {
  const { apiFetch } = useAuth();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const loadAds = useCallback(async () => {
    try {
      const data = await apiFetch('/api/super/ads');
      setAds(data.ads);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [apiFetch]);

  useEffect(() => { loadAds(); }, [loadAds]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setShowModal(true);
  };

  const openEdit = (ad) => {
    setEditing(ad);
    setForm({
      name: ad.name || '',
      placement: ad.placement || 'bottom',
      target_roles: ad.target_roles || ['coach', 'player', 'parent'],
      html_content: ad.html_content || '',
      priority: ad.priority || 10,
      start_date: ad.start_date ? ad.start_date.split('T')[0] : '',
      end_date: ad.end_date ? ad.end_date.split('T')[0] : '',
      fullpage_timer_seconds: ad.fullpage_timer_seconds || 5,
      fullpage_frequency: ad.fullpage_frequency || 10,
      status: ad.status || 'active',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        ...form,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        fullpage_timer_seconds: form.placement === 'fullpage' ? (form.fullpage_timer_seconds || 5) : null,
        fullpage_frequency: form.placement === 'fullpage' ? (form.fullpage_frequency || 10) : null,
      };
      if (editing) {
        await apiFetch(`/api/super/ads/${editing.id}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        await apiFetch('/api/super/ads', { method: 'POST', body: JSON.stringify(body) });
      }
      setShowModal(false);
      loadAds();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (ad) => {
    if (!window.confirm(`Delete ad "${ad.name}"?`)) return;
    try {
      await apiFetch(`/api/super/ads/${ad.id}`, { method: 'DELETE' });
      loadAds();
    } catch (err) { alert(err.message); }
  };

  const toggleRole = (role) => {
    setForm(prev => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role],
    }));
  };

  const formatDate = (d) => {
    if (!d) return '--';
    return new Date(d).toLocaleDateString();
  };

  return (
    <div>
      <SuperAdminNav activeItem="ads" />
      <div className="admin-page" style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 className="page-title" style={{ marginBottom: 0 }}>Manage Ads</h1>
          <button className="btn btn-orange" onClick={openAdd}>+ New Ad</button>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : ads.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>No ads yet. Create one to get started.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {ads.map(ad => (
              <div key={ad.id} className="card" style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <strong style={{ fontSize: '0.95rem' }}>{ad.name}</strong>
                      <span style={{
                        fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                        background: ad.status === 'active' ? 'rgba(46,204,113,0.15)' : 'rgba(243,156,18,0.15)',
                        color: ad.status === 'active' ? '#2ecc71' : '#f39c12',
                      }}>{ad.status}</span>
                      <span style={{
                        fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                        background: 'rgba(52,152,219,0.15)', color: '#3498db',
                      }}>{ad.placement}</span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      Targets: {(ad.target_roles || []).join(', ')} | Priority: {ad.priority} | {formatDate(ad.start_date)} - {formatDate(ad.end_date)}
                      {ad.placement === 'fullpage' && ` | Timer: ${ad.fullpage_timer_seconds}s | Every ${ad.fullpage_frequency} loads`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => openEdit(ad)}>Edit</button>
                    <button className="btn btn-sm btn-outline" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(ad)}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ marginBottom: 16 }}>{editing ? 'Edit Ad' : 'New Ad'}</h2>

            <div className="form-group">
              <label className="form-label">Name (internal)</label>
              <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Holiday Promo Banner" />
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Start Date</label>
                <input className="form-input" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">End Date</label>
                <input className="form-input" type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Target Users</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {TARGET_ROLES.map(r => (
                  <label key={r.value} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.target_roles.includes(r.value)} onChange={() => toggleRole(r.value)} />
                    {r.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Placement</label>
              <select className="form-input" value={form.placement} onChange={e => setForm({ ...form, placement: e.target.value })}>
                {PLACEMENTS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>

            {form.placement === 'fullpage' && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="form-label">Timer (seconds before X)</label>
                  <input className="form-input" type="number" min="0" value={form.fullpage_timer_seconds} onChange={e => setForm({ ...form, fullpage_timer_seconds: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="form-label">Show Every N Page Loads</label>
                  <input className="form-input" type="number" min="1" value={form.fullpage_frequency} onChange={e => setForm({ ...form, fullpage_frequency: parseInt(e.target.value) || 1 })} />
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">HTML Content</label>
              <textarea
                className="form-input"
                rows={8}
                value={form.html_content}
                onChange={e => setForm({ ...form, html_content: e.target.value })}
                placeholder="<div style='...'> ... </div>"
                style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Priority (1-100)</label>
                <input className="form-input" type="number" min="1" max="100" value={form.priority} onChange={e => setForm({ ...form, priority: parseInt(e.target.value) || 10 })} />
              </div>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Status</label>
                <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button className="btn btn-outline" onClick={() => setShowPreview(!showPreview)}>
                {showPreview ? 'Hide Preview' : 'Preview'}
              </button>
            </div>

            {showPreview && (
              <div style={{ border: '1px solid var(--card-border)', borderRadius: 8, padding: 16, marginBottom: 16, background: '#fff', color: '#000' }}>
                <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: 8 }}>Preview ({form.placement})</div>
                <div dangerouslySetInnerHTML={{ __html: form.html_content }} />
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-orange" onClick={handleSave} disabled={saving || !form.name || !form.html_content || form.target_roles.length === 0} style={{ flex: 1 }}>
                {saving ? 'Saving...' : (editing ? 'Update Ad' : 'Create Ad')}
              </button>
              <button className="btn btn-outline" onClick={() => setShowModal(false)} style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Fullpage Preview Overlay (if previewing a fullpage ad) */}
      {showPreview && form.placement === 'fullpage' && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowPreview(false)}>
          <div style={{ position: 'relative', maxWidth: 500, width: '90%' }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowPreview(false)} style={{ position: 'absolute', top: -40, right: 0, background: 'transparent', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer' }}>X</button>
            <div dangerouslySetInnerHTML={{ __html: form.html_content }} />
          </div>
        </div>
      )}
    </div>
  );
}
